import { createContext, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";

export const HamiltonianContext = createContext();

export function HamiltonianOperator({
  resolution,
  extent,
  potential,
  children,
}) {
  const dx = (2 * extent) / resolution; // Å
  const dt = 0.25 * dx * dx;
  const count = resolution ** 3;

  const psiRe = useRef(new Float32Array(count));
  const psiIm = useRef(new Float32Array(count));
  const colors = useRef(new Float32Array(count * 4));

  const [positions, setPositions] = useState(() => {
    let arr = new Float32Array(count * 3);
    let p = 0;
    for (let x = 0; x < resolution; x++)
      for (let y = 0; y < resolution; y++)
        for (let z = 0; z < resolution; z++) {
          arr[p++] = (x - (resolution - 1) / 2) * dx;
          arr[p++] = (y - (resolution - 1) / 2) * dx;
          arr[p++] = (z - (resolution - 1) / 2) * dx;
        }
    return arr;
  });

  const idx = (x, y, z) => x + resolution * (y + resolution * z);

  // Schrödinger evolution
  useFrame(() => {
    const newRe = new Float32Array(count);
    const newIm = new Float32Array(count);

    for (let x = 1; x < resolution - 1; x++)
      for (let y = 1; y < resolution - 1; y++)
        for (let z = 1; z < resolution - 1; z++) {
          const i = idx(x, y, z);

          const lapRe =
            psiRe.current[idx(x + 1, y, z)] +
            psiRe.current[idx(x - 1, y, z)] +
            psiRe.current[idx(x, y + 1, z)] +
            psiRe.current[idx(x, y - 1, z)] +
            psiRe.current[idx(x, y, z + 1)] +
            psiRe.current[idx(x, y, z - 1)] -
            6 * psiRe.current[i];

          const lapIm =
            psiIm.current[idx(x + 1, y, z)] +
            psiIm.current[idx(x - 1, y, z)] +
            psiIm.current[idx(x, y + 1, z)] +
            psiIm.current[idx(x, y - 1, z)] +
            psiIm.current[idx(x, y, z + 1)] +
            psiIm.current[idx(x, y, z - 1)] -
            6 * psiIm.current[i];

          newRe[i] =
            psiRe.current[i] +
            dt * (-lapIm / (dx * dx) - potential[i] * psiIm.current[i]);
          newIm[i] =
            psiIm.current[i] +
            dt * (lapRe / (dx * dx) + potential[i] * psiRe.current[i]);
        }

    psiRe.current = newRe;
    psiIm.current = newIm;
  });

  return (
    <HamiltonianContext.Provider
      value={{
        resolution: resolution,
        dx,
        idx,
        psiRe,
        psiIm,
        potential: potential,
        positions,
        colors,
        count,
      }}
    >
      <mesh scale={[2 * extent, 2 * extent, 2 * extent]}>
        <boxGeometry />
        <meshBasicMaterial color="#0090e6" wireframe />
      </mesh>
      <Html
        style={{ pointerEvents: "none", color: "#0090e6" }}
        position={[extent * 1.05, -extent, -extent * 1.05]}
        children={extent}
        center
      />
      <Html
        style={{ pointerEvents: "none", color: "#0090e6" }}
        position={[0, -extent, -extent * 1.05]}
        children={"Å"}
        center
      />
      <Html
        style={{ pointerEvents: "none", color: "#0090e6" }}
        position={[-extent * 1.05, -extent, -extent * 1.05]}
        children={-extent}
        center
      />
      <Html
        style={{ pointerEvents: "none", color: "#0090e6" }}
        position={[-extent * 1.05, -extent, 0]}
        children={"Å"}
        center
      />
      <Html
        style={{ pointerEvents: "none", color: "#0090e6" }}
        position={[-extent * 1.05, -extent, extent * 1.05]}
        children={-extent}
        center
      />
      {children}
    </HamiltonianContext.Provider>
  );
}
